import { useEffect, useRef, useCallback } from 'react';
import { ElementType } from '../../../../types/element.types';

/**
 * 重名检查 Hook
 * 用于 ElementTable 中的元素名称和简称字段的重名检查
 */
export interface DuplicateCheckOptions {
  /** 当前正在编辑的元素ID */
  editingItem: string | null;
  /** 当前正在编辑的字段（'name' | 'description'） */
  editingField: string | null;
  /** 编辑中的值（通过 ref 传递，避免依赖循环） */
  editValuesRef: React.MutableRefObject<{[key: string]: string}>;
  /** 所有场次元素列表 */
  elements: Array<{ elementID: string; elementName?: string; elementAbbr?: string; elementType?: ElementType }>;
  /** 重名检查函数 */
  onCheckDuplicate?: (
    elementId: string,
    elementName: string,
    elementAbbr?: string,
    elementType?: ElementType,
    checkField?: 'name' | 'description'
  ) => Promise<boolean>;
  /** 触发检查的回调函数（用于在输入值变化时调用） */
  onTriggerCheck?: () => void;
}

/**
 * 重名检查 Hook
 * 
 * 功能：
 * 1. 当字段进入编辑状态时，实时检查重名
 * 2. 当输入值变化时，实时检查重名
 * 3. 通过 DOM 属性标记重名状态，供全局快捷键处理器检查
 * 4. 更新输入框的 className 以显示错误样式
 */
export const useDuplicateCheck = ({
  editingItem,
  editingField,
  editValuesRef,
  elements,
  onCheckDuplicate,
  onTriggerCheck,
}: DuplicateCheckOptions) => {
  // 用于取消未完成的检查
  const cancelTokenRef = useRef<{ cancelled: boolean } | null>(null);

  /**
   * 更新 DOM 元素的重名状态
   */
  const updateDuplicateState = (element: HTMLElement | null, hasDuplicate: boolean) => {
    if (!element) return;
    
    if (hasDuplicate) {
      element.setAttribute('data-has-duplicate', 'true');
      element.classList.add('duplicate-error');
    } else {
      element.removeAttribute('data-has-duplicate');
      element.classList.remove('duplicate-error');
    }
  };

  /**
   * 执行重名检查
   */
  const performCheck = useCallback(async () => {
    // 只有在编辑名称或简称字段时才进行检查
    if (!editingItem || !editingField) {
      return;
    }

    if (editingField !== 'name' && editingField !== 'description') {
      return;
    }

    if (!onCheckDuplicate) {
      return;
    }

    // 查找当前编辑的元素
    const currentItem = elements.find(item => item.elementID === editingItem);
    if (!currentItem) {
      return;
    }

    // 获取当前输入值
    const nameKey = `${editingItem}-name`;
    const descriptionKey = `${editingItem}-description`;
    const latestEditValues = editValuesRef.current;
    
    const elementName = editingField === 'name' && latestEditValues[nameKey] !== undefined
      ? latestEditValues[nameKey]
      : (currentItem.elementName || '');
    
    const elementAbbr = editingField === 'description' && latestEditValues[descriptionKey] !== undefined
      ? latestEditValues[descriptionKey]
      : (currentItem.elementAbbr || '');

    // 查找对应的 DOM 元素
    const element = document.querySelector(
      `[data-item-id="${editingItem}"][data-field="${editingField}"]`
    ) as HTMLElement | null;

    // 先清除重名状态（乐观更新）
    updateDuplicateState(element, false);

    // 取消之前的检查
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancelled = true;
    }

    // 创建新的取消令牌
    const cancelToken = { cancelled: false };
    cancelTokenRef.current = cancelToken;

    // 根据正在编辑的字段决定只检查哪个字段
    const checkField: 'name' | 'description' | undefined = 
      editingField === 'name' ? 'name' : 
      editingField === 'description' ? 'description' : 
      undefined;

    try {
      // 执行异步检查
      const hasDuplicate = await onCheckDuplicate(
        editingItem,
        elementName,
        elementAbbr,
        currentItem.elementType,
        checkField
      );

      // 如果检查被取消，不更新状态
      if (cancelToken.cancelled) {
        return;
      }

      // 更新 DOM 状态
      const currentElement = document.querySelector(
        `[data-item-id="${editingItem}"][data-field="${editingField}"]`
      ) as HTMLElement | null;
      
      updateDuplicateState(currentElement, hasDuplicate);
    } catch (error) {
      console.error('❌ 重名检查失败:', error);
      
      // 如果检查被取消，不更新状态
      if (cancelToken.cancelled) {
        return;
      }

      // 清除错误状态
      const currentElement = document.querySelector(
        `[data-item-id="${editingItem}"][data-field="${editingField}"]`
      ) as HTMLElement | null;
      
      updateDuplicateState(currentElement, false);
    }
  }, [editingItem, editingField, onCheckDuplicate, elements, editValuesRef]);

  /**
   * 当编辑状态变化时，执行检查
   */
  useEffect(() => {
    // 如果不在编辑状态，清除所有重名标记
    if (!editingItem || !editingField) {
      // 清除所有可能的重名标记
      document.querySelectorAll('[data-has-duplicate]').forEach(el => {
        el.removeAttribute('data-has-duplicate');
        el.classList.remove('duplicate-error');
      });
      return;
    }

    // 如果不在编辑名称或简称字段，清除重名标记
    if (editingField !== 'name' && editingField !== 'description') {
      const element = document.querySelector(
        `[data-item-id="${editingItem}"][data-field="${editingField}"]`
      ) as HTMLElement | null;
      updateDuplicateState(element, false);
      return;
    }

    // 执行检查
    performCheck();

    // 清理函数：取消未完成的检查
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancelled = true;
      }
    };
  }, [editingItem, editingField, performCheck]);

  /**
   * 暴露 performCheck 函数供外部调用（当输入值变化时）
   */
  const triggerCheck = () => {
    performCheck();
  };

  // 通过回调函数暴露 triggerCheck
  useEffect(() => {
    if (onTriggerCheck) {
      // 将 triggerCheck 函数传递给父组件
      onTriggerCheck();
    }
  }, [onTriggerCheck]);

  // 将 triggerCheck 函数暴露到 ref 中，供外部调用
  useEffect(() => {
    if (editingItem && editingField && (editingField === 'name' || editingField === 'description')) {
      // 将函数存储到 ref 中，供 ElementTable 调用
      (editValuesRef as any).__triggerDuplicateCheck = triggerCheck;
    } else {
      delete (editValuesRef as any).__triggerDuplicateCheck;
    }
    // editValuesRef 是 ref，不需要在依赖数组中
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem, editingField, triggerCheck]);

  /**
   * 检查指定字段是否有重名
   * 供外部调用（如全局快捷键处理器）
   */
  const checkHasDuplicate = (itemId: string, field: string): boolean => {
    const element = document.querySelector(
      `[data-item-id="${itemId}"][data-field="${field}"]`
    ) as HTMLElement | null;
    
    if (!element) {
      return false;
    }

    return element.getAttribute('data-has-duplicate') === 'true';
  };

  return {
    checkHasDuplicate,
  };
};

